import type { Match, MatchId } from '@/core/schemas';

import type { MatchMutation } from './contracts';

/** A progression step that must not be written without a human looking at it. */
export interface BlockedMutation {
  readonly mutation: MatchMutation;
  /** The only reason so far: the follow-up match has already been played on. */
  readonly reason: 'match-in-progress';
}

export interface ProgressionSplit {
  readonly applicable: readonly MatchMutation[];
  readonly blocked: readonly BlockedMutation[];
}

/** Whether darts have been thrown for this match — or a result already exists. */
function hasBeenPlayed(match: Match): boolean {
  return (
    match.counter !== null ||
    match.status === 'running' ||
    match.status === 'completed' ||
    match.status === 'conflict'
  );
}

/**
 * Decides which progression steps may be written unattended.
 *
 * Filling an empty slot is always safe. *Replacing* a participant is not: if the
 * follow-up match has already been counted, silently swapping a team would
 * rewrite history behind the players' backs. Those steps are handed back as
 * blocked so the tournament director can decide — the one judgement the engine
 * must not make on its own.
 *
 * Pure on purpose: the browser (fallback) and the Cloud Function run exactly
 * the same decision.
 */
export function splitProgression(
  mutations: readonly MatchMutation[],
  matches: readonly Match[],
): ProgressionSplit {
  const byId = new Map<MatchId, Match>(matches.map((match) => [match.id, match]));
  const applicable: MatchMutation[] = [];
  const blocked: BlockedMutation[] = [];

  for (const mutation of mutations) {
    const target = byId.get(mutation.matchId);

    if (target === undefined) {
      continue;
    }

    const current = target[mutation.side];
    const isCorrection = current.kind === 'team';

    if (
      isCorrection &&
      mutation.participant.kind === 'team' &&
      current.teamId === mutation.participant.teamId
    ) {
      continue;
    }

    if (isCorrection && hasBeenPlayed(target)) {
      blocked.push({ mutation, reason: 'match-in-progress' });
      continue;
    }

    applicable.push(mutation);
  }

  return { applicable, blocked };
}
